import {useState} from 'react';

import {profileDetailsDataItemT, profileDetailsTabListTabT} from '@/lib/types/form.types';
import {orderT} from '@/lib/types/order.types';

import {RetailerDetailBrands} from '@/pages/(shared)/retailers/detail/components/retailerDetailBrands';
import {RetailerDetailMarkets} from '@/pages/(shared)/retailers/detail/components/retailerDetailMarkets';
import {useRetailer} from '@/pages/(shared)/retailers/detail/providers/retailerDetailsProvider';

import {
  LoadingSpinningLogoComponent,
  ProfileCardComponent,
  ProfileDetailsTabListComponent,
  profileCardSectionT,
} from '@/components';
import Page from '@/components/Page';

type pendingOrdersT = orderT & {order_status: 'pending'};
type inProgressOrdersT = orderT & {order_status: 'in-progress'};
type acceptedOrdersT = orderT & {order_status: 'accepted'};
type closedOrdersT = orderT & {order_status: 'closed'};

export function RetailerDetails() {
  const {retailer, isLoading} = useRetailer();

  // State
  const [orders] = useState<{
    pending: pendingOrdersT[];
    inProgress: inProgressOrdersT[];
    accepted: acceptedOrdersT[];
    closed: closedOrdersT[];
  }>({
    pending: [],
    inProgress: [],
    accepted: [],
    closed: [],
  });

  // Constants
  const headerInfo = {
    // Use the retailer name or a fallback if it's not available
    header: retailer?.retailer_name,

    // Use the creation date as a subheader
    subHeader: retailer?.date_created
      ? `Established on ${new Date(retailer.date_created).toLocaleDateString()} - ${retailer.date_removed ? `Deleted on ${new Date(retailer.date_removed).toLocaleDateString()}` : 'Active Retailer'}`
      : 'Date not available',

    // Include dynamic content in perks, like city and state, and any other relevant perks
    perks: [
      {
        icon: 'ri-shield-check-line',
        content: retailer?.retailer_license_number ? 'Licensed Retailer' : 'Unlicensed Retailer',
      },
      {
        icon: 'ri-map-pin-line',
        content: `${retailer?.retailer_city || 'City'}, ${retailer?.retailer_state || 'State'}`,
      },
    ],
  };

  const sections: profileCardSectionT[] = [
    {
      title: 'Retailer Information',
      items: [
        {header: 'Name', content: retailer?.retailer_name},
        {header: 'Description', content: 'Retailer Description'},
        {header: 'License Number', content: retailer?.retailer_license_number},
      ],
    },
    {
      title: 'Location Information',
      items: [
        {
          header: 'Address',
          content: `${retailer?.retailer_address}, ${retailer?.retailer_address2 || ''}`.trim(),
        },
        {header: 'City', content: retailer?.retailer_city},
        {header: 'State', content: retailer?.retailer_state},
        {header: 'Postal Code', content: retailer?.retailer_postal_code},
      ],
    },
    {
      title: 'Contact Information',
      items: [
        {header: 'Phone', content: retailer?.retailer_phone},
        {header: 'Email', content: retailer?.retailer_email},
      ],
    },
  ];

  const tabs: profileDetailsTabListTabT[] = [
    {
      id: 'markets',
      title: 'Markets',
      icon: 'ri-shopping-cart-2-line',
      content: <RetailerDetailMarkets />,
    },

    {
      id: 'brands',
      title: 'Brands',
      icon: 'ri-store-2-line',
      content: <RetailerDetailBrands />,
    },
  ];

  const myDataItems: profileDetailsDataItemT[] = [
    {
      type: 'generic',
      title: 'Pending Orders',
      data: orders.pending.map((order: pendingOrdersT) => ({
        item: order,
        texts: [
          {text1: 'Order Date:', text2: new Date(order.date_created).toLocaleDateString()},
          {text1: 'Order Status:', text2: order.order_status},
          {text1: 'Order Balance:', text2: `$${order.order_balance}`},
        ],
      })),
    },
    {
      type: 'generic',
      title: 'In Progress Orders',
      data: orders.inProgress.map((order: inProgressOrdersT) => ({
        item: order,
        texts: [
          {text1: 'Order Date:', text2: new Date(order.date_created).toLocaleDateString()},
          {text1: 'Order Status:', text2: order.order_status},
          {text1: 'Order Balance:', text2: `$${order.order_balance}`},
        ],
      })),
    },
    {
      type: 'generic',
      title: 'Accepted/Rejected Orders',
      data: [...orders.accepted].map((order: acceptedOrdersT) => ({
        item: order,
        texts: [
          {text1: 'Order Date:', text2: new Date(order.date_created).toLocaleDateString()},
          {text1: 'Order Status:', text2: order.order_status},
          {text1: 'Order Balance:', text2: `$${order.order_balance}`},
        ],
      })),
    },
  ];

  // TODO: Add a loading state. Skeleton loaders for the profile card and tab list?
  if (isLoading || !retailer) return <LoadingSpinningLogoComponent />;

  return (
    <Page currentPage="Retailers">
      <div className="grid grid-cols-12 gap-x-6">
        <ProfileCardComponent
          avatar={'https://placekeanu.com/100/100'}
          name={retailer.retailer_name}
          headerInfo={headerInfo}
          sections={sections}
          status={retailer?.retailer_status}
        />
        <ProfileDetailsTabListComponent
          tabs={tabs}
          dataItems={myDataItems}
          className="col-span-12 xl:col-span-12 xxl:col-span-8"
        ></ProfileDetailsTabListComponent>
      </div>
    </Page>
  );
}
